import {useDisclosure} from '@mantine/hooks';
import type {MRT_ColumnDef} from 'mantine-react-table';
import React, {useMemo, useState} from 'react';
import type {ClientSimpleResponse} from 'src/api/ProactApiClient';
import {ModuleEnum, PermissionOperationEnum} from 'src/api/ProactApiClient';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {Pill} from 'src/design/atoms/Pill';
import {ActiveInactivePills} from 'src/shared/constants/PillConstants';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {ActiveStateDataOptions} from 'src/shared/constants/columnFilterData';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {SidebarLinkClients} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {
  useDeleteClientMutation,
  useGetClientListV2Query,
} from 'src/redux/queries/client';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export const ClientTable: React.FC = () => {
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const [deleteTarget, setDeleteTarget] = useState<ClientSimpleResponse>();
  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [deleteClient] = useDeleteClientMutation();

  const columns = useMemo<MRT_ColumnDef<ClientSimpleResponse>[]>(
    () => [
      {
        accessorKey: 'active',
        header: 'Active',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: ActiveStateDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...ActiveInactivePills(cell.getValue<boolean>())} />
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        id: 'location',
        accessorFn: row => [row.city, row.state].filter(Boolean).join(', '),
        header: 'City/State',
        enableColumnFilter: false,
        enableSorting: false,
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'client',
    columns: columns,
    defaultColumnFilters: [
      {
        id: 'active',
        value: ['true'],
      },
    ],
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'name', desc: false}],
    defaultShowColumnFilters: true,
  });
  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetClientListV2Query({
      start: start,
      length: length,
      filters: filters,
      filterFns: filterFns,
      sorting: sorting,
    });
  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const handleDelete = async () => {
    if (!deleteTarget) return;

    confirmDeleteHandlers.close();
    setIsDeleting(true);

    try {
      await deleteClient(deleteTarget.id).unwrap();
      setAlert(
        `${deleteTarget.name ?? 'Client'} was successfully deleted.`,
        'success'
      );
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsDeleting(false);
    setDeleteTarget(undefined);
  };

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      getRowId: row => String(row.id),
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View
            onClick={() => navigate(PathUtility.ClientsView(row.original.id))}
          />
          <PermissionGuard
            module={ModuleEnum.Clients}
            operation={PermissionOperationEnum.Delete}>
            <TableRowActions.Menu>
              <TableRowActions.MenuItem
                color="red"
                onClick={() => {
                  setDeleteTarget(row.original);
                  confirmDeleteHandlers.open();
                }}>
                Delete
              </TableRowActions.MenuItem>
            </TableRowActions.Menu>
          </PermissionGuard>
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            <PermissionGuard
              module={ModuleEnum.Clients}
              operation={PermissionOperationEnum.Create}>
              <TableToolbar.Action
                variant="create"
                onClick={() => navigate(PathUtility.ClientsCreate())}>
                New Client
              </TableToolbar.Action>
            </PermissionGuard>
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <>
      <PageHeader>
        <PageHeader.Title
          icon={SidebarLinkClients.icon}
          subtitle="Manage client companies">
          Clients
        </PageHeader.Title>
      </PageHeader>
      {table}

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDelete}
        confirmationButtonColor="red"
        confirmationButtonDisabled={isDeleting}
        title="Are you sure you want to delete this client?"
        body="This action cannot be undone. Any sites associated with this client will be unassociated, not deleted."
      />
    </>
  );
};
